import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Building2, User, DollarSign, FileText, Calendar } from "lucide-react";
import { ControleProjetoForm } from "./ControleProjetoForm";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { projectArea } from "@/utils/project-area";
import { CancelarRascunhoButton } from "./CancelarRascunhoButton";

export interface ProjetoRascunho {
  id: string;
  agencia_id: string;
  freelancer_id: string;
  area_atuacao: string;
  area_canonica?: string | null;
  valor_projeto: number | null;
  vaga_id: string | null;
  data_inicio: string | null;
  data_conclusao: string | null;
  nucleo: string | null;
  created_at: string;
  agencia?: { nome: string } | null;
  freelancer?: { nome: string } | null;
}

interface RascunhosDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rascunhos: ProjetoRascunho[];
  onPromote: (projetoData: Record<string, unknown>, rascunhoId: string) => Promise<void> | void;
  onSaveDraft: (projetoData: Record<string, unknown>, rascunhoId: string) => Promise<void> | void;
}

const formatBRL = (valor: number | null | undefined): string => {
  if (valor === null || valor === undefined) return "—";
  return valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

export function RascunhosDialog({
  open,
  onOpenChange,
  rascunhos,
  onPromote,
  onSaveDraft,
}: RascunhosDialogProps) {
  const [selecionado, setSelecionado] = useState<ProjetoRascunho | null>(null);
  const [modo, setModo] = useState<"promote" | "edit">("promote");

  const handleSave = async (projetoData: Record<string, unknown>) => {
    if (!selecionado) return;
    if (modo === "promote") {
      await onPromote(projetoData, selecionado.id);
    } else {
      await onSaveDraft(projetoData, selecionado.id);
    }
    setSelecionado(null);
  };

  return (
    <>
      <Dialog
        open={open && !selecionado}
        onOpenChange={(o) => {
          if (!o) setSelecionado(null);
          onOpenChange(o);
        }}
      >
        <DialogContent className="sm:max-w-[700px] max-h-[80vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-amber-600" />
              Projetos em Rascunho
            </DialogTitle>
            <DialogDescription>
              Estes projetos foram criados ao aprovar vagas. Complete os dados
              restantes para ativá-los.
            </DialogDescription>
          </DialogHeader>

          {/* O DialogContent só tem max-h (altura indefinida), então o h-full do viewport
              do Radix não resolve e a lista é cortada sem rolar: o limite vai no viewport. */}
          <ScrollArea
            type="always"
            className="flex-1 min-h-0 -mx-6 px-6 *:data-radix-scroll-area-viewport:max-h-[min(60vh,calc(80vh-10rem))]"
          >
            {rascunhos.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">
                Nenhum rascunho pendente.
              </p>
            ) : (
              <ul className="space-y-2 py-2">
                {rascunhos.map((r) => (
                  <li
                    key={r.id}
                    className="flex items-center justify-between gap-4 rounded-lg border border-slate-200 p-3 hover:border-amber-300 transition-colors"
                  >
                    <div className="flex-1 min-w-0 space-y-1 text-sm">
                      <div className="flex items-center gap-2 font-medium text-slate-900">
                        <Building2 className="h-3.5 w-3.5 text-slate-500" />
                        <span className="truncate">
                          {r.agencia?.nome ?? "Sem agência"}
                        </span>
                        {projectArea(r) && (
                          <span className="text-xs text-slate-500">
                            • {projectArea(r)}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
                        <span className="inline-flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {r.freelancer?.nome ?? "Sem freelancer"}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <DollarSign className="h-3 w-3" />
                          {formatBRL(r.valor_projeto)}
                        </span>
                        {r.vaga_id && (
                          <span className="inline-flex items-center gap-1">
                            <FileText className="h-3 w-3" />
                            Vaga #{r.vaga_id.substring(0, 6)}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Criado em{" "}
                          {format(new Date(r.created_at), "dd/MM/yyyy", {
                            locale: ptBR,
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <CancelarRascunhoButton projetoId={r.id} freelancerNome={r.freelancer?.nome} temVaga={!!r.vaga_id} />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setModo("edit");
                          setSelecionado(r);
                        }}
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setModo("promote");
                          setSelecionado(r);
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        Completar
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!selecionado}
        onOpenChange={(o) => {
          if (!o) setSelecionado(null);
        }}
      >
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {modo === "promote" ? "Completar Projeto" : "Editar Rascunho"}
            </DialogTitle>
            <DialogDescription>
              {modo === "promote"
                ? "Preencha os campos restantes (em especial a data de início) para ativar o projeto."
                : "Ajuste os dados do projeto. Ele continua como rascunho até você completá-lo."}
            </DialogDescription>
          </DialogHeader>
          {selecionado && (
            <ControleProjetoForm
              projeto={selecionado}
              mode={modo}
              onSave={handleSave}
              onCancel={() => setSelecionado(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
